// src/components/WeightSummary.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Flex, Heading, Text, Stack, Button, ButtonGroup,
    useColorModeValue, useDisclosure, Tooltip
} from '@chakra-ui/react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    ReferenceDot, ResponsiveContainer
} from 'recharts';
import { useAuth } from '../../../contexts/AuthContext';
import {
    fetchWeightEntries,
    estimateTDEE
} from '../../../data/WeightEntries';
import {LogWeightModal} from './LogWeightModal';
import {InfoOutlineIcon} from "@chakra-ui/icons";

const KG_TO_LB = 2.20462;

export default function WeightSummary() {
    const { user } = useAuth();
    const [entries, setEntries]   = useState([]);
    const [tdeeInfo, setTdeeInfo] = useState(null);
    const [unit, setUnit]         = useState('lb');
    const { isOpen, onOpen, onClose } = useDisclosure();

    // helper to reload both entries + TDEE
    const loadData = () => {
        fetchWeightEntries(user.id).then(setEntries);
        estimateTDEE(user.id).then(setTdeeInfo);
    };

    useEffect(() => {
        if (!user) return;
        loadData();
    }, [user]);

    // last 7 days of weights, converted to the chosen unit
    const last7 = useMemo(() => {
        const today  = new Date();
        const cutoff = new Date(today);
        cutoff.setDate(today.getDate() - 6);

        return entries
            .filter(e => new Date(e.date) >= cutoff)
            .sort((a,b) => new Date(a.date) - new Date(b.date))
            .map(e => {
                const raw = +e.weight_kg;
                const w   = unit === 'kg'
                    ? raw
                    : +(raw * KG_TO_LB).toFixed(1);
                return {
                    date: new Date(e.date).toLocaleDateString('en-US',{ month:'short', day:'numeric' }),
                    weight: w
                };
            });
    }, [entries, unit]);

    // compute start/current/pctChange/label
    const { startWeight, currentWeight, pctChange, label } = useMemo(() => {
        if (!last7.length) return {};
        const start = last7[0].weight;
        const curr  = last7[last7.length - 1].weight;
        const change = ((curr - start) / start) * 100;
        let lbl = 'Steady Vibes ✌️';
        if (change <= -1) lbl = 'Shedding Pounds 🔥';
        if (change >= 1)  lbl = 'Gains Mode 🚀';
        return {
            startWeight: start.toFixed(1),
            currentWeight: curr.toFixed(1),
            pctChange: change.toFixed(1),
            label: lbl
        };
    }, [last7]);

    const bg        = useColorModeValue('white','gray.700');
    const noteColor = useColorModeValue('gray.600','gray.400');
    const unitLabel = unit === 'kg' ? 'kg' : 'lb';
    return (
        <Box bg={bg} p={6} rounded="lg" shadow="md">
            <Stack spacing={4}>
                {/* Header + unit toggle */}
                <Flex justify="space-between" align="center">
                    <Heading size="md">Weight Trend</Heading>
                    <ButtonGroup size="sm" isAttached variant="outline">
                        <Button
                            variant={unit === 'kg' ? 'solid' : 'outline'}
                            onClick={() => setUnit('kg')}
                        >
                            kg
                        </Button>
                        <Button
                            variant={unit === 'lb' ? 'solid' : 'outline'}
                            onClick={() => setUnit('lb')}
                        >
                            lb
                        </Button>
                    </ButtonGroup>
                </Flex>

                {/* No entries prompt */}
                {entries.length === 0 ? (
                    <Box textAlign="center" py={8}>
                        <Text mb={4}>You haven’t logged any weights yet.</Text>
                    </Box>
                ) : (
                    <>
                        {/* Sparkline */}
                        <Box h="140px" px={4} pt={2} pb={1}>
                            <ResponsiveContainer width="100%" height="100%">
                                <Box h="140px">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={last7} margin={{ top: 0, right: 15, left: 10, bottom: 0 }}>
                                            {/* 1. light grid */}
                                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />

                                            {/* 2. show X and Y axes with small ticks */}
                                            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                            <YAxis
                                                domain={['dataMin - 1', 'dataMax + 1']}
                                                tick={{ fontSize: 10 }}
                                                width={30}
                                            />

                                            {/* 4. gradient fill definition */}
                                            <defs>
                                                <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#3182CE" stopOpacity={0.3}/>
                                                    <stop offset="95%" stopColor="#3182CE" stopOpacity={0}/>
                                                </linearGradient>
                                            </defs>

                                            {/* 5. the line itself */}
                                            <Line
                                                type="monotone"
                                                dataKey="weight"
                                                stroke="#3182CE"
                                                strokeWidth={2}
                                                dot={{ r: 3 }}
                                                activeDot={{ r: 5, strokeWidth: 2 }}
                                                fill="url(#grad)"
                                                isAnimationActive={true}
                                                animationDuration={800}
                                            />

                                            {/* 6. highlight the last point */}
                                            {last7.length > 0 && (
                                                <ReferenceDot
                                                    x={last7[last7.length - 1].date}
                                                    y={last7[last7.length - 1].weight}
                                                    r={6}
                                                    fill="#3182CE"
                                                    stroke="white"
                                                    strokeWidth={2}
                                                />
                                            )}
                                        </LineChart>
                                    </ResponsiveContainer>
                                </Box>
                            </ResponsiveContainer>
                        </Box>

                        {/* Stats */}
                        <Stack direction="row" justify="space-between">
                            <Text>Start: <strong>{startWeight} {unitLabel}</strong></Text>
                            <Text>Now:   <strong>{currentWeight} {unitLabel}</strong></Text>
                            <Text>Δ %:   <strong>{pctChange}%</strong></Text>
                        </Stack>

                        <Text fontWeight="semibold">{label}</Text>

                        {/* TDEE info */}
                        {tdeeInfo && (
                            <Stack spacing={2}>
                                <Tooltip
                                    label="Calories your body burns every day just by being alive (at rest)."
                                    fontSize="sm"
                                    openDelay={300}
                                >
                                    <Text fontSize="sm" display="flex" alignItems="center">
                                        Maintenance: <strong>{Math.round(tdeeInfo.maintenance)} kcal/day</strong>
                                        <InfoOutlineIcon ml={1} boxSize="4" color="gray.500" />
                                    </Text>
                                </Tooltip>

                                <Tooltip
                                    label="Calories you need each day to keep your current weight."
                                    fontSize="sm"
                                    openDelay={300}
                                >
                                    <Text fontSize="sm" display="flex" alignItems="center">
                                        BMR: <strong>{Math.round(tdeeInfo.bmr)} kcal/day</strong>
                                        <InfoOutlineIcon ml={1} boxSize="4" color="gray.500" />
                                    </Text>
                                </Tooltip>

                                <Tooltip
                                    label="How active you are. Higher means you burn more calories through movement. 1=sedentary, 1.5=moderate, 1.8=very active."
                                    fontSize="sm"
                                    openDelay={300}
                                >
                                    <Text fontSize="sm" display="flex" alignItems="center">
                                        Activity Factor:  <strong>{tdeeInfo.activityFactor.toFixed(2)}</strong>
                                        <InfoOutlineIcon ml={1} boxSize="4" color="gray.500" />
                                    </Text>
                                </Tooltip>
                            </Stack>
                        )}
                    </>
                )}

                {/* Bottom button */}
                <Button size="sm" variant="outline" onClick={onOpen}>
                    {entries.length === 0 ? 'Log first weight' : 'Log new weight'}
                </Button>
            </Stack>

            {/* Externalized modal */}
            <LogWeightModal
                isOpen={isOpen}
                unit={unit}
                onClose={onClose}
                profileId={user.id}
                onSuccess={() => {
                    onClose();
                    loadData();
                }}
            />
        </Box>
    );
}